import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Newspaper,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Eye,
  ExternalLink,
  X,
  Upload,
  CheckCircle2,
  AlertCircle,
  FileText,
  Send,
} from "lucide-react";
import {
  fetchAdminNews,
  createAdminNews,
  updateAdminNews,
  deleteAdminNews,
  uploadAdminNewsPhoto,
  TYPE_LABELS,
  STATUS_LABELS,
} from "@/api/news";
import RichTextEditor from "@/components/admin/RichTextEditor";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const emptyForm = {
  title: "",
  type: "blog",
  body_html: "",
  excerpt: "",
  image: "",
  author: "Lemmer Leeft",
  status: "draft",
};

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-xs font-[Poppins] font-semibold uppercase tracking-wide text-[#5A7594] block mb-1.5">
      {label}
    </span>
    {children}
    {hint && <span className="text-xs text-[#8AA3B8] block mt-1">{hint}</span>}
  </label>
);

const Toast = ({ toast }) =>
  toast ? (
    <div
      data-testid="admin-news-toast"
      className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl shadow-lg font-[Poppins] font-semibold text-sm flex items-center gap-2 ${
        toast.type === "error" ? "bg-[#8b1420] text-white" : "bg-[#173A5E] text-white"
      }`}
    >
      {toast.type === "error" ? (
        <AlertCircle className="w-4 h-4" />
      ) : (
        <CheckCircle2 className="w-4 h-4" />
      )}
      {toast.message}
    </div>
  ) : null;

const previewUrl = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith("/api/")) return `${BACKEND_URL}${v}`;
  return v;
};

const NewsFormModal = ({ initial, onClose, onSaved, showToast }) => {
  const isNew = !initial;
  const [form, setForm] = useState(() => ({ ...emptyForm, ...(initial || {}) }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (nextStatus) => {
    if (!form.title.trim()) {
      showToast("Titel is verplicht", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, status: nextStatus };
      if (isNew) {
        const created = await createAdminNews(payload);
        showToast(nextStatus === "published" ? "Gepubliceerd" : "Concept opgeslagen");
        onSaved(created);
      } else {
        const res = await updateAdminNews(initial.id, payload);
        showToast(
          nextStatus === "published"
            ? `"${res.post.title}" gepubliceerd`
            : `"${res.post.title}" bijgewerkt`,
        );
        onSaved(res.post);
      }
    } catch (err) {
      showToast(err?.response?.data?.detail || "Opslaan mislukt", "error");
    } finally {
      setSaving(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isNew) {
      showToast("Sla eerst het bericht op, upload dan de afbeelding.", "error");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadAdminNewsPhoto(initial.id, file);
      set("image", res.image);
      showToast("Afbeelding geüpload");
    } catch (err) {
      showToast(err?.response?.data?.detail || "Upload mislukt", "error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const preview = previewUrl(form.image);

  return (
    <div
      data-testid="news-form-modal"
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
    >
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl my-8">
        <div className="sticky top-0 bg-white border-b border-[#E4EEF5] px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-xl font-bold text-[#173A5E] font-[Poppins]">
            {isNew ? "Nieuw bericht" : `Bewerken: ${initial.title}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="news-form-close"
            className="p-2 rounded-lg hover:bg-[#F4F8FB]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="md:col-span-2">
            <Field label="Titel *">
              <input
                type="text"
                required
                data-testid="news-form-title"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 focus:border-[#6BC0E8] outline-none font-[Poppins] text-lg"
              />
            </Field>
          </div>
          <Field label="Type *">
            <select
              data-testid="news-form-type"
              value={form.type}
              onChange={(e) => set("type", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 bg-white"
            >
              <option value="blog">Blog</option>
              <option value="persbericht">Persbericht</option>
            </select>
          </Field>

          <div className="md:col-span-3">
            <Field
              label="Featured afbeelding"
              hint={isNew ? "Sla eerst op als concept, upload dan een afbeelding." : "JPG/PNG/WEBP · vervangt bestaande."}
            >
              <div className="flex items-start gap-4 flex-wrap">
                {preview && (
                  <img
                    src={preview}
                    alt="preview"
                    data-testid="news-form-image-preview"
                    className="w-40 h-28 object-cover rounded-lg border border-[#E4EEF5]"
                  />
                )}
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={onPhoto}
                    disabled={isNew || uploading}
                    data-testid="news-form-image-file"
                    className="text-sm"
                  />
                  <input
                    type="text"
                    value={form.image}
                    onChange={(e) => set("image", e.target.value)}
                    placeholder="of /media/… of https://…"
                    className="w-80 max-w-full border border-[#E4EEF5] rounded-lg px-3 py-2 text-xs"
                  />
                  {uploading && (
                    <span className="text-xs text-[#2E9CD6] flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Bezig met uploaden…
                    </span>
                  )}
                </div>
              </div>
            </Field>
          </div>

          <div className="md:col-span-3">
            <Field label="Introtekst (excerpt)" hint="Kort samenvatting voor overzicht en social preview. Blijft leeg → automatisch uit body.">
              <textarea
                data-testid="news-form-excerpt"
                rows={2}
                value={form.excerpt}
                onChange={(e) => set("excerpt", e.target.value)}
                className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2 font-[Inter]"
              />
            </Field>
          </div>

          <div className="md:col-span-3">
            <Field label="Bericht (rich text)">
              <RichTextEditor
                value={form.body_html}
                onChange={(html) => set("body_html", html)}
                onError={(m) => showToast(m, "error")}
              />
            </Field>
          </div>

          <Field label="Auteur">
            <input
              type="text"
              data-testid="news-form-author"
              value={form.author}
              onChange={(e) => set("author", e.target.value)}
              className="w-full border border-[#E4EEF5] rounded-lg px-3 py-2"
            />
          </Field>
          <div className="md:col-span-2 flex items-end">
            <div className="text-xs text-[#5A7594] font-[Inter]">
              {initial ? (
                <>
                  Slug: <code className="bg-[#F4F8FB] px-1.5 py-0.5 rounded">{initial.slug}</code>
                  {initial.published_at && (
                    <> · Gepubliceerd op {new Date(initial.published_at).toLocaleDateString("nl-NL")}</>
                  )}
                </>
              ) : (
                "Slug wordt automatisch gegenereerd uit de titel."
              )}
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#E4EEF5] px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={() => submit("draft")}
            disabled={saving}
            data-testid="news-form-save-draft"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F4F8FB] hover:bg-[#E4EEF5] text-[#173A5E] font-[Poppins] font-semibold text-sm disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Concept opslaan
          </button>
          <button
            type="button"
            onClick={() => submit("published")}
            disabled={saving}
            data-testid="news-form-publish"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#2A7A3F] hover:bg-[#20602F] text-white font-[Poppins] font-semibold text-sm disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Publiceren
          </button>
        </div>
      </div>
    </div>
  );
};

export const AdminNewsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = "ok") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      const data = await fetchAdminNews(params);
      setItems(data.items || []);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Ophalen mislukt", "error");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { draft: 0, published: 0 };
    for (const it of items) c[it.status] = (c[it.status] || 0) + 1;
    return c;
  }, [items]);

  const doDelete = async (item) => {
    try {
      await deleteAdminNews(item.id);
      setItems((prev) => prev.filter((p) => p.id !== item.id));
      showToast(`"${item.title}" verwijderd`);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Verwijderen mislukt", "error");
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div data-testid="admin-news-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#173A5E] font-[Poppins] flex items-center gap-2">
            <Newspaper className="w-7 h-7 text-[#2E9CD6]" />
            Nieuws
          </h1>
          <p className="text-sm text-[#5A7594] mt-1">
            Blog & persberichten. {counts.published} gepubliceerd · {counts.draft} concept.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="btn-new-news"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#173A5E] hover:bg-[#0F2A46] text-white font-[Poppins] font-semibold text-sm shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Nieuw bericht
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E4EEF5] p-4 mb-4 flex flex-wrap gap-3">
        <select
          data-testid="admin-news-filter-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-[#E4EEF5] rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Alle statussen</option>
          <option value="published">Gepubliceerd</option>
          <option value="draft">Concept</option>
        </select>
        <select
          data-testid="admin-news-filter-type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border border-[#E4EEF5] rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">Alle types</option>
          <option value="blog">Blog</option>
          <option value="persbericht">Persbericht</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#2E9CD6]" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-[#5A7594] py-16 text-center">Nog geen berichten. Klik op “Nieuw bericht” om te beginnen.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E4EEF5] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F8FB] text-[#173A5E] font-[Poppins]">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Titel</th>
                  <th className="text-left px-4 py-3 font-semibold">Type</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Datum</th>
                  <th className="text-right px-4 py-3 font-semibold">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E4EEF5] font-[Inter]">
                {items.map((it) => (
                  <tr key={it.id} data-testid={`news-row-${it.slug}`} className="hover:bg-[#F9FBFD]">
                    <td className="px-4 py-3">
                      <div className="font-[Poppins] font-semibold text-[#173A5E]">{it.title}</div>
                      <div className="text-xs text-[#8AA3B8]">{it.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                          it.type === "persbericht"
                            ? "bg-[#F4E9D8] text-[#8B6914]"
                            : "bg-[#E1F0FA] text-[#0F5C8A]"
                        }`}
                      >
                        {TYPE_LABELS[it.type] || it.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                          it.status === "published"
                            ? "bg-[#E6F5EA] text-[#2A7A3F]"
                            : "bg-[#F4F8FB] text-[#5A7594]"
                        }`}
                      >
                        {STATUS_LABELS[it.status] || it.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#5A7594] text-xs">
                      {it.published_at
                        ? new Date(it.published_at).toLocaleDateString("nl-NL")
                        : it.updated_at
                        ? new Date(it.updated_at).toLocaleDateString("nl-NL")
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {it.status === "published" && (
                          <a
                            href={`/nieuws/${it.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`news-view-${it.slug}`}
                            title="Bekijk publieke pagina"
                            className="p-2 rounded-lg text-[#2E9CD6] hover:bg-[#F4F8FB]"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => setEditing(it)}
                          data-testid={`news-edit-${it.slug}`}
                          className="p-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB]"
                          title="Bewerken"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(it)}
                          data-testid={`news-delete-${it.slug}`}
                          className="p-2 rounded-lg text-[#8b1420] hover:bg-[#FDECEE]"
                          title="Verwijderen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(creating || editing) && (
        <NewsFormModal
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            load();
          }}
          showToast={showToast}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" data-testid="news-confirm-delete">
            <h3 className="text-xl font-bold text-[#173A5E] font-[Poppins] mb-2">Verwijderen bevestigen</h3>
            <p className="text-sm text-[#5A7594] mb-4">
              Weet je zeker dat je <strong>{confirmDelete.title}</strong> wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg text-[#173A5E] hover:bg-[#F4F8FB] font-[Poppins] font-semibold text-sm"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={() => doDelete(confirmDelete)}
                data-testid="news-confirm-delete-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#8b1420] hover:bg-[#6a0f18] text-white font-[Poppins] font-semibold text-sm"
              >
                <Trash2 className="w-4 h-4" />
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
};

export default AdminNewsPage;
